import { HTMLAttributes, useEffect, useState, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

import { Modal } from '../../../common';
import { VideoModalProps } from './video-modal.models';
import { useStyles } from './video-modal.styles';
import { v2026PP13733 } from '../../../../constants/veeva';
import { ExternalLinks } from '../../../../constants';
import { Preloader } from '../../../common/preloader';
import { cx } from '../../../../utils';

export const VideoModal = ({ mobileVideoSrc, desktopVideoSrc, videoId, ...props }: VideoModalProps): JSX.Element => {
  const classes = useStyles();
  const [isLoading, setIsLoading] = useState(true);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const theme = useTheme();
  const isMobileWidth = useMediaQuery(theme.breakpoints.down('sm'));
  const isPortrait = useMediaQuery('(orientation: portrait)');
  const isMobilePortrait = isMobileWidth && isPortrait;

  useEffect(() => {
    const handlePlayerMessage = (event: { origin: string; source: any; data: any; }): void => {
      const videoUrl = ExternalLinks.yandexVideoUrl;
      // 1. Security check: Only listen to messages coming from Yandex Cloud Video
      if (event.origin !== videoUrl) {
        return;
      }

      // 2. Make sure the message matches our specific active iframe
      // @ts-ignore
      if (iframeRef.current && iframeRef.current.contentWindow) {
        const data = event.data;

        if (data) {
          const formattedData = JSON.parse(data);

          // 3. Listen for the initialization ('inited') event
          if (formattedData && formattedData.event === 'inited') {
            console.log('Yandex Cloud Video Player ready. Forcing 1080p...');

            // 4. Send postMessage safely targeting the specific origin
            iframeRef.current.contentWindow.postMessage(
              {
                method: 'setQuality',
                quality: 'hd1080',
              },
              videoUrl,
            );
          }
        }
      }
    };

    // Attach event listener on mount
    window.addEventListener('message', handlePlayerMessage);

    // Clean up the event listener on unmount
    return () => {
      window.removeEventListener('message', handlePlayerMessage);
    };
  }, [videoId]);

  const handleIframeLoad = (): void => {
    setIsLoading(false);
  };

  return (
    <Modal
      {...props}
      PaperProps={{ 'data-testid': 'neuro-modal' } as HTMLAttributes<HTMLDivElement>}
      propsContentStyles={classes.content}
      propsPaperStyles={classes.paper}
      veevaNumber={v2026PP13733}
      isVeevaSmall
      content={(
        <>
          <h2 className={classes.title} data-testid="neuro-modal-title">
            <FormattedMessage id="neuro.title" />
          </h2>
          <div className={classes.wrapper}>
            {isLoading && <Preloader isInner />}
            {isMobilePortrait ? (
              <iframe
                id={videoId}
                ref={iframeRef}
                title={`${videoId} portrait video player`}
                width="315"
                height="560"
                src={mobileVideoSrc}
                allow="autoplay; fullscreen; accelerometer; gyroscope; picture-in-picture; encrypted-media; screen-wake-lock"
                data-testid="neuro-modal-iframe"
                frameBorder="0"
                scrolling="no"
                className={cx(classes.frame, classes.framePortrait)}
                onLoad={handleIframeLoad}
              ></iframe>
            ) : (
              <iframe
                id={videoId}
                ref={iframeRef}
                title={`${videoId} landscape video player`}
                width="560"
                height="315"
                src={desktopVideoSrc}
                allow="autoplay; fullscreen; accelerometer; gyroscope; picture-in-picture; encrypted-media; screen-wake-lock"
                data-testid="neuro-modal-iframe"
                frameBorder="0"
                scrolling="no"
                className={cx(classes.frame, classes.frameLandscape)}
                onLoad={handleIframeLoad}
              ></iframe>
            )}
          </div>
        </>
      )}
    />
  );
};
